import { useMemo } from "react";
import { motion } from "motion/react";
import { Calendar, ListTodo, Sparkles, Users } from "lucide-react";
import { Counter, Stagger, StaggerItem } from "@/components/FadeIn";
import { M3Card, M3CardHeader } from "@/components/M3Shared";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { usePublishModuleSlot } from "@/lib/ModuleContext";
import { useSupabaseQuery, useRealtimeTable } from "@/lib/dbHooks";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/types/db";

type ModuleId = "today" | "famchat" | "fammatch" | "famtree" | "famstatus" | "famlist";
type CheckInRow   = Database["public"]["Tables"]["check_ins"]["Row"];
type TodoItemRow  = Database["public"]["Tables"]["todo_items"]["Row"];
type NotifRow     = Database["public"]["Tables"]["notifications"]["Row"];

export function TodayView({ navigate }: { navigate: (id: ModuleId) => void }) {
  const me = useCurrentUser();
  const userId = me.user?.id ?? null;
  const cf = useCurrentFamily(userId);
  const memberIds = cf.members.map((member) => member.id);
  const memberIdsKey = memberIds.join(",");

  // Today's check-ins across members.
  const checkIns = useSupabaseQuery<CheckInRow[]>(async () => {
    if (!cf.family?.id) return { data: null, error: null };
    const today = new Date().toISOString().slice(0, 10);
    return await supabase
      .from("check_ins")
      .select("*")
      .in("member_id", memberIds)
      .eq("checked_on", today);
  }, [cf.family?.id, memberIdsKey]);

  // Next todo due (soonest upcoming).
  const nextTodo = useSupabaseQuery<TodoItemRow | null>(async () => {
    if (!cf.list?.id) return { data: null, error: null };
    return await supabase
      .from("todo_items")
      .select("*")
      .eq("list_id", cf.list.id)
      .eq("done", false)
      .not("due_at", "is", null)
      .order("due_at", { ascending: true })
      .limit(1)
      .maybeSingle();
  }, [cf.list?.id]);

  // Recent notifications (top 5).
  const notifs = useSupabaseQuery<NotifRow[]>(async () => {
    if (!userId) return { data: null, error: null };
    return await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(5);
  }, [userId]);

  useRealtimeTable<"notifications">(
    "notifications",
    `user_id=eq.${userId ?? ""}`,
    () => notifs.refresh(),
  );

  // Right-rail context for `xl+` (wide desktop). Summarises the
  // data already on the home page in a denser, scannable form so
  // the user has quick reference points while their main attention
  // is on the body. Re-built only when the underlying data shifts,
  // not on every render.
  const todayDate = new Date().toISOString().slice(0, 10);
  const checkedTodayCount = (checkIns.data ?? []).filter((c) => c.checked_on === todayDate).length;
  const todaySlot = useMemo(() => {
    if (!cf.family) return null;
    return {
      pageTitle: "今日",
      pageSubtitle: cf.family.name,
      context: (
        <div className="flex flex-col gap-3">
          <M3Card className="border-border/40">
            <M3CardHeader
              title="今日概要"
              icon={<Calendar className="h-4 w-4" aria-hidden />}
              trailing={`${checkedTodayCount}/${cf.members.length}`}
            />
            <ul className="mt-3 space-y-2.5 text-sm">
              <li className="flex items-center gap-2.5">
                <span aria-hidden className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                  ✓
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground">今日已打卡</div>
                  <div className="font-semibold tabular-nums">{checkedTodayCount} / {cf.members.length}</div>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <span aria-hidden className="grid h-8 w-8 place-items-center rounded-lg bg-rose-100 text-rose-700">
                  <Users className="h-4 w-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground">家庭成員</div>
                  <div className="font-semibold tabular-nums">{cf.members.length} 位</div>
                </div>
              </li>
            </ul>
          </M3Card>
          {nextTodo.data && (
            <M3Card className="border-border/40">
              <M3CardHeader
                title="下一件要做嘅事"
                icon={<ListTodo className="h-4 w-4" aria-hidden />}
              />
              <button
                type="button"
                onClick={() => navigate("famlist")}
                className="mt-3 flex w-full items-start gap-2.5 rounded-2xl border border-border/40 bg-card p-3 text-left transition-colors hover:bg-secondary"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{nextTodo.data.text}</div>
                  {nextTodo.data.due_at && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {formatTime(nextTodo.data.due_at)}
                    </div>
                  )}
                </div>
              </button>
            </M3Card>
          )}
        </div>
      ),
    };
  }, [cf.family, cf.members.length, checkedTodayCount, nextTodo.data, navigate]);
  usePublishModuleSlot(todaySlot);

  if (cf.needsBootstrap) {
    return (
      <div className="px-1 pt-1 text-center">
        <Sparkles className="mx-auto mt-12 h-10 w-10 text-primary" aria-hidden />
        <h1 className="mt-4 font-serif text-2xl font-semibold">歡迎，{me.displayName}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          系統正在為你建立家庭…
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          如果這畫面停留太久，請重新整理頁面。
        </p>
      </div>
    );
  }

  if (cf.loading && !cf.family) {
    return (
      <div className="px-1 pt-1 text-sm text-muted-foreground">載入中…</div>
    );
  }

  const today = new Date();
  const dateLabel = new Intl.DateTimeFormat("zh-HK", {
    weekday: "long", month: "long", day: "numeric",
  }).format(today);

  const checkedToday = new Set(
    (checkIns.data ?? []).filter(c => c.state === "done").map(c => c.member_id)
  );
  const pending = cf.members.filter(m => !checkedToday.has(m.id));
  const allChecked = pending.length === 0 && cf.members.length > 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.header
        className="px-1 pt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs text-muted-foreground sm:text-sm">{dateLabel}</p>
        <h1 className="mt-1 font-serif text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          你好，<span className="text-primary">{me.displayName}</span>。
        </h1>
        <p className="mt-2 text-pretty text-sm text-muted-foreground sm:text-base">
          這是你家 <span className="font-medium text-foreground">{cf.family.name}</span> 今天的樣子。
          {cf.members.length > 0 && ` 共 ${cf.members.length} 位成員。`}
        </p>
      </motion.header>

      {/* Quick check-in CTA. Mood log surface has folded into
          FamStatus — we don't repeat it on the home view; users
          who want to record a feeling can do so inside the
          打卡 flow they already live in. */}
      {!allChecked && (
        <button
          type="button"
          onClick={() => navigate("famstatus")}
          className="group flex w-full items-center gap-3 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 to-accent/20 p-3 text-left transition-colors hover:border-primary/60"
        >
          <span
            aria-hidden
            className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-sm shadow-primary/30"
          >
            ✓
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              FamStatus
            </div>
            <div className="truncate text-sm font-semibold">
              今日 {pending.length} 位未打卡 · 輕輕一撳同屋企人講「我好好」
            </div>

          </div>
          <span className="shrink-0 text-xs font-medium text-primary group-hover:underline">
            去打卡 →
          </span>
        </button>
      )}

      {/* Notifications feed */}
      <M3Card>
        <M3CardHeader
          title="家人動態"
          subtitle="最新通知"
          icon={<Counter to={(notifs.data ?? []).length} />}
        />
        {((notifs.data ?? []).length === 0 && !notifs.loading) ? (
          <p className="mt-3 text-sm text-muted-foreground">
            暫無新動態。
          </p>
        ) : (
          <Stagger as="ul" className="mt-3 space-y-2.5" gap={0.07}>
            {(notifs.data ?? []).map(n => (
              <StaggerItem
                key={n.id}
                as="li"
                className="flex items-start gap-3 rounded-2xl bg-secondary/40 p-3"
              >
                <span
                  aria-hidden
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-card text-lg shadow-sm"
                >
                  {iconFor(n.kind)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground/85">{kindLabel(n.kind)}</span>
                  </div>
                  <div className="mt-0.5 text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{n.body}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground/80">
                    {formatRelative(n.created_at)}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </M3Card>

      {/* Next todo — single-card override of the todaySlot */}
      {nextTodo.data && (
        <M3Card>
          <M3CardHeader
            title="下一件要做嘅事"
            subtitle={cf.list?.name ?? "FamList"}
            icon={<ListTodo className="h-4 w-4" aria-hidden />}
          />
          <button
            type="button"
            onClick={() => navigate("famlist")}
            className="mt-3 flex w-full items-start gap-3 rounded-2xl border border-border/40 bg-card p-3 text-left transition-colors hover:bg-secondary"
          >
            <span
              aria-hidden
              className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-rose-100 text-rose-700"
            >
              🛒
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{nextTodo.data.text}</div>
              {nextTodo.data.due_at && (
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {formatTime(nextTodo.data.due_at)}
                </div>
              )}
            </div>
            <span className="shrink-0 text-xs font-medium text-primary">
              開清單 →
            </span>
          </button>
        </M3Card>
      )}

      {/* Footer */}
      <p className="px-2 pt-2 text-center text-[11px] leading-relaxed text-muted-foreground">
        © {new Date().getFullYear()} FamChest 傳琛 · All rights reserved.
      </p>
    </div>
  );
}

function iconFor(kind: string): string {
  switch (kind) {
    case "chat":   return "💬";
    case "status": return "✓";
    case "tree":   return "🌳";
    // FamMood folded into FamStatus; the mood kind now shows the
    // status check so old notification rows aren't blank.
    case "mood":   return "✓";
    case "list":   return "📝";
    case "match":  return "🤝";
    case "tag":    return "🏷️";
    default:       return "🔔";
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case "chat":   return "FamChat";
    case "status": return "FamStatus";
    case "list":   return "FamList";
    case "tree":   return "FamTree";
    case "mood":   return "FamStatus · 心情";
    case "match":  return "FamMatch";
    case "tag":    return "@提及";
    default:       return "系統";
  }
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "剛剛";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分鐘前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小時前`;
  return new Intl.DateTimeFormat("zh-HK", { month: "numeric", day: "numeric" }).format(new Date(iso));
}

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat("zh-HK", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}
